import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, Link, Navigate } from 'react-router-dom';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PrinterOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useIRN, useApproveIRN, useRejectIRN, useCancelIRN } from '@/hooks/useQCWorkflow';
import { useUsers } from '@/hooks/useUsers';
import { buildUserIdToLabelMap, resolveUserDisplayFromMap } from '@/utils/userDisplay';
import { IrnInspectorDetailsScreen } from '@/components/quality/IrnInspectorDetails';

const { Title } = Typography;
const { TextArea } = Input;

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    Draft: 'default',
    'Pending Approval': 'processing',
    Approved: 'success',
    Rejected: 'error',
    Cancelled: 'default',
  };
  return colors[status] || 'default';
}

const InspectionReleaseNoteDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rejectForm] = Form.useForm();
  const [approveForm] = Form.useForm();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);

  const { data: irn, isLoading, isError, refetch } = useIRN(id);
  const { data: users = [] } = useUsers({ is_active: true });
  const userDisplayMap = useMemo(() => buildUserIdToLabelMap(users), [users]);
  const ru = (uid?: string | null) => resolveUserDisplayFromMap(userDisplayMap, uid, '—');

  const approveMutation = useApproveIRN();
  const rejectMutation = useRejectIRN();
  const cancelMutation = useCancelIRN();

  const openPrint = () => {
    window.open(`/print/qms/inspection-release-notes/${id}`, '_blank', 'noopener,noreferrer');
  };

  const canApproveReject = irn?.status === 'Pending Approval';
  const canCancel =
    irn && irn.status !== 'Approved' && irn.status !== 'Cancelled';

  if (!id) {
    return <Navigate to="/qms/inspection-release-notes" replace />;
  }

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !irn) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" showIcon message="IRN not found" description="Check the link or return to the list." />
        <Button style={{ marginTop: 16 }} onClick={() => navigate('/qms/inspection-release-notes')}>
          Back to list
        </Button>
      </div>
    );
  }

  const woId = irn.work_order_id || irn.workOrder?.id || irn.deliveryNote?.workOrder?.id;
  const woNum = irn.workOrder?.work_order_number || irn.deliveryNote?.workOrder?.work_order_number;
  const dnId = irn.delivery_note_id || irn.deliveryNote?.id;
  const dnNum = irn.deliveryNote?.delivery_note_number;
  const firId = irn.fir_id || irn.fir?.id;
  const firNum = irn.fir?.fir_number;

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/qms/dashboard">QMS</Link> },
          { title: <Link to="/qms/inspection-release-notes">Inspection release notes</Link> },
          { title: irn.irn_number },
        ]}
      />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space wrap align="center">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/inspection-release-notes')}>
            List
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {irn.irn_number}
          </Title>
          <Tag color={getStatusColor(irn.status)}>{irn.status}</Tag>
          <Button icon={<PrinterOutlined />} onClick={openPrint}>
            Print
          </Button>
          {canApproveReject && (
            <>
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => setApproveOpen(true)}>
                Approve
              </Button>
              <Button danger icon={<CloseCircleOutlined />} onClick={() => setRejectOpen(true)}>
                Reject
              </Button>
            </>
          )}
          {canCancel && (
            <Button
              icon={<StopOutlined />}
              loading={cancelMutation.isPending}
              onClick={() => {
                Modal.confirm({
                  title: 'Cancel this IRN?',
                  content: 'This cannot be used for dispatch while cancelled.',
                  okText: 'Cancel IRN',
                  okButtonProps: { danger: true },
                  onOk: () => cancelMutation.mutateAsync(irn.id).then(() => refetch()),
                });
              }}
            >
              Cancel IRN
            </Button>
          )}
        </Space>

        <Card title="Traceability">
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Work order">
              {woId && woNum ? (
                <Link to={`/mfg/work-orders/${woId}`}>{woNum}</Link>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Delivery note">
              {dnId && dnNum ? (
                <Link to={`/growth/delivery-notes/${dnId}`}>{dnNum}</Link>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Final inspection request">
              {firId && firNum ? (
                <Link to={`/qms/final-inspection-reports/${firId}?source=fir_request`}>{firNum}</Link>
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <IrnInspectorDetailsScreen irn={irn} />

        <Card title="Inspection release note">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="IRN number">{irn.irn_number}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(irn.status)}>{irn.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Release quantity">
              {irn.release_quantity != null && !Number.isNaN(Number(irn.release_quantity))
                ? Number(irn.release_quantity).toFixed(2)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Release date">
              {irn.release_date ? new Date(irn.release_date).toLocaleDateString() : '—'}
            </Descriptions.Item>
            {irn.released_by && (
              <Descriptions.Item label="Released by">
                {irn.releaser?.name || ru(irn.released_by)}
              </Descriptions.Item>
            )}
            {irn.approved_date && (
              <>
                <Descriptions.Item label="Approved date">
                  {new Date(irn.approved_date).toLocaleDateString()}
                </Descriptions.Item>
                <Descriptions.Item label="Approved by">
                  {irn.approver?.name || ru(irn.approved_by)}
                </Descriptions.Item>
              </>
            )}
            {irn.rejection_reason && (
              <Descriptions.Item label="Rejection reason" span={2}>
                {irn.rejection_reason}
              </Descriptions.Item>
            )}
            {irn.notes && (
              <Descriptions.Item label="Notes" span={2}>
                {irn.notes}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {irn.status === 'Approved' && (
          <Alert
            type="success"
            showIcon
            message="IRN approved"
            description="Linked delivery note may proceed to in transit when other requirements are met."
          />
        )}
        {irn.status === 'Rejected' && (
          <Alert
            type="error"
            showIcon
            message="IRN rejected"
            description="Resolve quality concerns and create or resubmit an IRN from the delivery note before shipping."
          />
        )}
      </Space>

      <Modal
        title="Approve IRN"
        open={approveOpen}
        onCancel={() => {
          setApproveOpen(false);
          approveForm.resetFields();
        }}
        okText="Approve"
        confirmLoading={approveMutation.isPending}
        onOk={() => approveForm.submit()}
      >
        <Form
          form={approveForm}
          layout="vertical"
          onFinish={async (values: { notes?: string }) => {
            await approveMutation.mutateAsync({ id: irn.id, notes: values.notes?.trim() || undefined });
            setApproveOpen(false);
            approveForm.resetFields();
            refetch();
          }}
        >
          <Form.Item name="notes" label="Approval notes (optional)">
            <TextArea rows={3} placeholder="Optional notes stored on the IRN" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reject IRN"
        open={rejectOpen}
        onCancel={() => {
          setRejectOpen(false);
          rejectForm.resetFields();
        }}
        okText="Reject"
        confirmLoading={rejectMutation.isPending}
        onOk={() => rejectForm.submit()}
      >
        <Form
          form={rejectForm}
          layout="vertical"
          onFinish={async (values: { rejectionReason: string }) => {
            await rejectMutation.mutateAsync({
              id: irn.id,
              rejectionReason: values.rejectionReason,
            });
            setRejectOpen(false);
            rejectForm.resetFields();
            refetch();
          }}
        >
          <Form.Item
            name="rejectionReason"
            label="Rejection reason"
            rules={[{ required: true, message: 'Please enter a rejection reason' }]}
          >
            <TextArea rows={4} placeholder="Reason for rejection" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InspectionReleaseNoteDetailPage;
